/**
 * Shared branch-aware modifier/size resolution for POS + Waiter.
 *
 * Single source of truth so Cashier POS and Waiter resolve the SAME:
 *   Branch -> Item -> enabled sizes -> branch size price -> add-ons
 *
 * Reuses the existing data already on the menu item:
 *   - `_resolved_modifiers` (backend-resolved) or legacy `modifiers`
 *   - `modifier_group.branch_availability` ({branchId: [sizeName|addonId,...]})
 *   - `branch_prices["{sizeName}__{branchId}"]` — per-size branch price
 *
 * Pricing semantics are UNCHANGED: the returned size option `price` is still
 * the additive size amount the existing cart logic adds on top of the base
 * item price. We only swap in the branch-specific size price when configured,
 * falling back to the global size price otherwise.
 */
export function resolveBranchModifiers(item, branchId) {
  const all = item?._resolved_modifiers || item?.modifiers || [];
  const bp = item?.branch_prices || {};

  const visible = all
    .filter((group) => {
      const ba = group.branch_availability || {};
      const keys = Object.keys(ba);
      if (keys.length === 0) return true; // available everywhere
      if (!branchId) return true;
      if (group.type === 'option') return ba[branchId] === true || ba[branchId] === undefined;
      if (group.type === 'size' || group.type === 'addon') {
        const allowed = ba[branchId];
        return Array.isArray(allowed) && allowed.length > 0;
      }
      return true;
    })
    .map((group) => {
      const ba = group.branch_availability || {};
      let g = group;
      // Filter options to those enabled for this branch.
      if (branchId && Array.isArray(ba[branchId])) {
        const allowed = ba[branchId];
        if (group.type === 'size') {
          g = { ...group, options: (group.options || []).filter((o) => allowed.includes(o.name)) };
        } else if (group.type === 'addon') {
          g = { ...group, options: (group.options || []).filter((o) => allowed.includes(o.addon_id || o.name)) };
        }
      }
      // Per-size branch price: prefer branch_prices["{name}__{branch}"], else global.
      if (g.type === 'size' && branchId) {
        g = {
          ...g,
          options: (g.options || []).map((o) => {
            const bv = bp[`${o.name}__${branchId}`];
            return bv !== undefined && bv !== null && bv !== ''
              ? { ...o, price: Number(bv) }
              : o;
          }),
        };
      }
      return g;
    })
    .filter((group) => (group.options || []).length > 0);

  // De-duplicate groups by name — PREFER the typed (size/addon) branch-aware
  // group over a legacy same-named modifier (backend emits both). Then
  // de-duplicate options by name (keep the higher price).
  const byNameGroup = new Map();
  for (const g of visible) {
    const k = (g.name || '').trim().toLowerCase();
    const existing = byNameGroup.get(k);
    const typed = g.type === 'size' || g.type === 'addon';
    if (!existing) { byNameGroup.set(k, g); continue; }
    const exTyped = existing.type === 'size' || existing.type === 'addon';
    if (typed && !exTyped) byNameGroup.set(k, g);
  }
  return Array.from(byNameGroup.values())
    .map((g) => {
      const byName = new Map();
      (g.options || []).forEach((o) => {
        const ok = (o.name || '').trim().toLowerCase();
        const ex = byName.get(ok);
        if (!ex) { byName.set(ok, o); return; }
        if ((Number(o.price) || 0) > (Number(ex.price) || 0)) byName.set(ok, o);
      });
      return { ...g, options: Array.from(byName.values()) };
    });
}
